import { useStableCallback } from '@confluence/use-stable/entry-points/useStableCallback';

import type { FetchOptions } from './fetchResponse';
import { fetchResponse } from './fetchResponse';

// Allows using variables from either the mutate call or initial hook call
// Follows Apollo Client behavior: only falls back when variables is undefined, not empty object
const resolveVariables = <TVariables>(
	localVariables: TVariables | undefined,
	fallbackVariables: TVariables | undefined,
): TVariables => {
	return localVariables !== undefined ? localVariables : (fallbackVariables ?? ({} as TVariables));
};

export const useFetchData = <TData, TVariables, TResponse>({
	url,
	method,
	variables = {} as TVariables,
	headers,
	body,
	credentials,
	parseResult,
	parseResponseOnSuccess,
	parseResponseOnError,
}: FetchOptions<TVariables, TResponse> & {
	variables?: TVariables;
	parseResult: (response: TResponse, variables: TVariables) => TData | Promise<TData>;
}) => {
	const fetchData = useStableCallback(async (localVariables?: TVariables): Promise<TData> => {
		const resolvedVariables = resolveVariables(localVariables, variables);

		const response = await fetchResponse<TVariables, TResponse>(
			{
				url,
				method,
				body,
				headers,
				credentials,
				parseResponseOnSuccess,
				parseResponseOnError,
			},
			resolvedVariables,
		);

		return parseResult(response, resolvedVariables);
	});

	return { fetchData };
};

// Fetch multiple endpoints and parse their responses
export const useFetchDataWithMultipleFetches = <TData, TVariables, TResponse>({
	requestConfigs,
	variables = {} as TVariables,
	parseResult,
}: {
	requestConfigs: FetchOptions<TVariables, TResponse>[];
	variables?: TVariables;
	parseResult: (responses: TResponse[], variables: TVariables) => TData | Promise<TData>;
}) => {
	const fetchData = useStableCallback(async (localVariables?: TVariables): Promise<TData> => {
		const resolvedVariables = resolveVariables(localVariables, variables);

		const responses = await Promise.all(
			requestConfigs.map((fetchOptions) =>
				fetchResponse<TVariables, TResponse>(fetchOptions, resolvedVariables),
			),
		);

		return parseResult(responses, resolvedVariables);
	});

	return { fetchData };
};
